import { activityLabelMap, ASSET_SUM_ONB_ID, SORT_OPTION } from '../constants';
import {
  autoObj,
  splitter,
  valueFilterOptions,
  workOrderOptions,
} from '../views/containers/SiteListMap/components/utils';

export const getFilterProfileAttribute = (
  filterProfileData,
  selectedFilterProfile,
) =>
  filterProfileData?.filterProfiles?.find(
    profile => profile?.filterCode === selectedFilterProfile,
  )?.attributes;

export const getFilterProfileListForCategory = (filterAttributes, category) => {
  const requiredFilterAttr = filterAttributes?.filter(
    filterAttr => filterAttr?.attributeName === category,
  );
  if (requiredFilterAttr?.length) {
    return [...requiredFilterAttr[0]?.attributeValues];
  }
  return [];
};

export const applySearchKeyFilter = (list, searchKey) => {
  if (!searchKey?.genre || !searchKey?.title) return list;
  const parts = searchKey.title.split(splitter);
  const filters = {
    [autoObj.country]: ele => ele?.cn === searchKey.title,
    [autoObj.state]: ele => ele?.cn === parts[0] && ele?.st === parts[1],
    [autoObj.city]: ele =>
      ele?.cn === parts[0] && ele?.st === parts[1] && ele?.ct === parts[2],
    [autoObj.sites]: ele =>
      ele?.cc === parts[0] &&
      ele?.dn === parts[1] &&
      ele?.ct === parts[2] &&
      ele?.st === parts[3] &&
      ele?.cn === parts[4],
  };
  const filterFn = filters[searchKey.genre] || (() => true);
  return list.filter(filterFn);
};
/**
 * Returns sum of all count values in site's assetSum except ONB.
 * Used for high/low sort; ONB count is ignored as it can be wrong.
 * assetSum is an array of { id, dn, count }.
 */
export const getSiteAssetCountExcludingOnb = site => {
  const assetSum = site?.assetSum;
  if (!Array.isArray(assetSum)) return 0;
  return assetSum.reduce((sum, item) => {
    if (item?.id === ASSET_SUM_ONB_ID) return sum;
    const count = item?.count;
    const n = typeof count === 'number' ? count : parseInt(count, 10);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
};

/**
 * Comparator for sorting sites alphabetically by display name (dn).
 */
export const compareSitesByDn = (a, b) =>
  (a?.dn || '').localeCompare(b?.dn || '', undefined, { sensitivity: 'base' });

/**
 * Compare a value against a target using the given operator code.
 * Operator codes match valueFilterOptions from SiteListMap/utils.
 */
export const compareWithOperator = (actual, operator, target) => {
  switch (operator) {
    case 'equals':
      return actual === target;
    case 'nonequals':
      return actual !== target;
    case 'lessthan':
      return actual < target;
    case 'lessthanorequals':
      return actual <= target;
    case 'greaterthan':
      return actual > target;
    case 'greaterthanorequals':
      return actual >= target;
    default:
      return true;
  }
};

/**
 * Calculate the opportunity percentage for a site: the proportion of assets
 * in the given combo statuses relative to total onboarded (ONB).
 */
export const getSiteOpportunityPercentage = (site, comboStatus) => {
  const assetSum = site?.assetSum;
  if (!Array.isArray(assetSum)) return 0;
  const statuses = (comboStatus || '').split('|').filter(Boolean);
  const onbItem = assetSum.find(item => item?.id === ASSET_SUM_ONB_ID);
  const onbCount = parseInt(onbItem?.count, 10) || 0;
  if (onbCount === 0) return 0;
  const statusCount = assetSum
    .filter(item => statuses.includes(item?.id))
    .reduce((sum, item) => sum + (parseInt(item?.count, 10) || 0), 0);
  return (statusCount / onbCount) * 100;
};

/**
 * Check if a site matches the work order filter condition.
 * Sums woNp / woCmp / woInp from assetSum items matching comboStatus.
 */
export const checkWorkOrderFilter = (assetSum, comboStatus, filterOption) => {
  const statuses = (comboStatus || '').split('|').filter(Boolean);
  const filtered = (assetSum || []).filter(item => statuses.includes(item?.id));
  let woNp = 0;
  let woCmp = 0;
  let woInp = 0;
  filtered.forEach(item => {
    woNp += parseInt(item?.woNp, 10) || 0;
    woCmp += parseInt(item?.woCmp, 10) || 0;
    woInp += parseInt(item?.woInp, 10) || 0;
  });
  switch (filterOption) {
    case 'none':
      return woCmp === 0 && woInp === 0;
    case 'some':
      return woNp > 0 && (woCmp > 0 || woInp > 0);
    default:
      return true;
  }
};

/**
 * Check if a site matches the activity filter condition.
 * Sums actp / actNp from assetSum items matching comboStatus.
 */
export const checkActivityFilter = (assetSum, comboStatus, filterOption) => {
  const statuses = (comboStatus || '').split('|').filter(Boolean);
  const filtered = (assetSum || []).filter(item => statuses.includes(item?.id));
  let actp = 0;
  let actNp = 0;
  filtered.forEach(item => {
    actp += parseInt(item?.actp, 10) || 0;
    actNp += parseInt(item?.actNp, 10) || 0;
  });
  switch (filterOption) {
    case 'none':
      return actp === 0;
    case 'some':
      return actNp > 0;
    default:
      return true;
  }
};

/**
 * Returns CSS rotation for sort icon (deg). Do not change degree values.
 * Low to high → upward (0deg); High to low → downward (180deg).
 */
export const getSortIconRotation = sortOption => {
  if (sortOption === SORT_OPTION.LOW_TO_HIGH) return '0deg';
  if (sortOption === SORT_OPTION.HIGH_TO_LOW) return '180deg';
  return '0deg';
};

export const buildTrendFilterLabel = (rowData, t) => {
  const parts = [];
  if (rowData?.percent_key && rowData?.percent_value) {
    const opOption = valueFilterOptions.find(
      o => o.mobileSupportedValue === rowData.percent_key,
    );
    if (opOption) {
      parts.push(`${t(opOption.label)} ${rowData.percent_value}%`);
    }
  }
  if (rowData?.work_order_id) {
    const woOption = workOrderOptions.find(
      o => o.mobileSupportedValue === rowData.work_order_id,
    );
    if (woOption) parts.push(t(woOption.label));
  }
  if (rowData?.activity_id && activityLabelMap[rowData.activity_id]) {
    parts.push(t(activityLabelMap[rowData.activity_id]));
  }
  return parts.length > 0 ? `-${parts.join('-')}` : '';
};
